import { catchError, map, take } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import {
    AccountSelectors,
    ModulesSelectors,
    SosCoreConfig,
    TrackingActions,
} from '@serviceos-ng/core';
import { combineLatest, of } from 'rxjs';
import { ScriptService } from 'ngx-script-loader';

declare global {
    interface Window {
        fcWidget: any;
        fcWidgetMessengerConfig: any;
        fwcrm: any;
    }
}

@Injectable({
    providedIn: 'root',
})
export class FreshChatService {
    private scriptLoad = false;
    private chatConfig: any;
    private callback: Function | null = null;

    constructor(
        private store: Store,
        private scriptLoaderService: ScriptService,
        private config: SosCoreConfig
    ) {}

    initialize(callback: Function) {
        if (callback && typeof callback === 'function') this.callback = callback;

        this.store
            .select(ModulesSelectors.getModuleByName('chat'))
            .subscribe((config) => {
                if (
                    config?.settings?.providers?.freshchat &&
                    !this.scriptLoad
                ) {
                    this.chatConfig = config.settings.providers.freshchat;
                    this.scriptLoaderService
                        .loadScript(this.chatConfig.freshchat_url)
                        .pipe(
                            take(1),
                            map(() => true),
                            catchError(() => of(false))
                        )
                        .subscribe((isLoader) => {
                            this.scriptLoad = isLoader;
                            if (isLoader) {

                                const chatUrl = new URL(this.chatConfig.freshchat_url);
                                
                                this.store.selectOnce(AccountSelectors.account)
                                    .subscribe(userSnapShot => {
                                        if(userSnapShot) {
                                            const restoreId = userSnapShot?.username;
        
                                            window.fcWidgetMessengerConfig = {
                                                tags: ["bot_naomi"],
                                                externalId: userSnapShot?.username,
                                                restoreId: restoreId ? restoreId : null
                                            }
                                        }
        
        
                                        window.fcWidget.init({
                                            // When chat is initialize most likely we still d'not have the user email.
                                            // externalId: "{{base64_encode($layoutData['user']->username)}}",
                                            token: this.chatConfig.freshchat_token,
                                            host: chatUrl?.origin,
                                            tags: ["bot_naomi"],     // trigger_development tag is for development testing only, Setting FAQ Tags in the object below.
                                            faqTags : {
                                                // Array of Tags
                                                tags : ['bot_naomi'], // trigger_development tag is for development testing only
                                                //For articles, the below value should be article.
                                                //For article category, the below value should be category.
                                                filterType:'category' //Or filterType: 'article'
                                            },
                                            config: {
                                                headerProperty: {
                                                    hideChatButton: true,
                                                    siteId: window.location.origin,
                                                },
                                            },
                                        });
        
        
        
                                        // Subscribe for user data to set user properties.
                                        this.updateUserChatInformation();
                                        // Feed data to tracking.
                                        this.updateTracking();

                                    });
                            }
                        });
                }
            });
    }

    open() {
        if (window.fcWidget && window.fcWidget.isOpen() != true) {
            window.fcWidget.open();
        }
    }

    private updateUserChatInformation() {
        combineLatest([
            this.store.select(AccountSelectors.account),
            this.store.select(
                ModulesSelectors.getSetting([
                    'phone',
                    'dialing_code',
                    'country_code',
                ])
            ),
        ])
            .pipe(
                map(([user, settings]: any) => {
                    const endpoint = this.config.api_url.split('/api')[0];

                    if (user && settings) {
                        const phone = user.phones?.find(
                            (phone) => phone.default
                        );

                        return {
                            firstName: user.first_name,
                            lastName: user.last_name,
                            email: user.username,
                            phone: phone?.value || '-',
                            cf_dialing_code: settings.dialing_code,
                            cf_user_type: user?.membership
                                ? 'member'
                                : 'not a member',
                            cf_membership_expire_date: user?.membership?.valid_to_formatted || '-',
                            cf_xrm_status: `${endpoint}/order?answer=&client_phone=${phone?.value || 'N/A'}&company_phone=${settings.phone}`,
                            cf_clientidentkey: user?.client_ident_key || '-',
                        };
                    } else {
                        return null;
                    }
                })
            )
            .subscribe((userData) => {
                if(userData) window.fcWidget.user.setProperties(userData);
            });
    }

    private updateTracking() {
        window.fcWidget.on('widget:opened', (e) => {
            if (this.callback && typeof this.callback === 'function') {
                this.callback({
                    type: 'track',
                    data: {
                        event: 'Freshchat Interaction',
                        freshchatAction: 'Widget Open'
                    }
                });
            }
        });

        window.fcWidget.on('widget:closed', (e) => {
            if (this.callback && typeof this.callback === 'function') {
                this.callback({
                    type: 'track',
                    data: {
                        event: 'Freshchat Interaction',
                        freshchatAction: 'Widget Close'
                    }
                });
            }
        });

        window.fcWidget.on('message:received', (e) => {
            if (this.callback && typeof this.callback === 'function') {
                this.callback({
                    type: 'track',
                    data: {
                        event: 'Freshchat Interaction',
                        freshchatAction: 'Received'
                    }
                });
            }
        });

        window.fcWidget.on('message:sent', (e) => {
            if (this.callback && typeof this.callback === 'function') {
                this.callback({
                    type: 'track',
                    data: {
                        event: 'Freshchat Interaction',
                        freshchatAction: 'Sent'
                    }
                });
            }
        });

        window.fcWidget.on('user:statechange', (e) => {
            if (e?.data && this.callback && typeof this.callback === 'function') {
                switch (e.data.userState) {
                    case 'created':
                        this.callback({
                            type: 'track',
                            data: {
                                event: 'Freshchat Interaction',
                                freshchatAction: 'User Create',
                            }
                        });

                        break;
                    case 'loaded':
                        this.callback({
                            type: 'track',
                            data: {
                                event: 'Freshchat Interaction',
                                freshchatAction: 'User Loaded',
                            }
                        });

                        break;
                    case 'identified':
                        this.callback({
                            type: 'track',
                            data: {
                                event: 'Freshchat Interaction',
                                freshchatAction: 'User Identified',
                            }
                        });

                        break;
                    case 'restored':
                        this.callback({
                            type: 'track',
                            data: {
                                event: 'Freshchat Interaction',
                                freshchatAction: 'User Restored'
                            }
                        });

                        break;
                }
            }
        });
    }
}
